import React from 'react';
import { Checkbox, CheckboxGroup, DateInput, Grid, GridItem, Heading, Radio, RadioGroup, TextArea, semantics, typography, useHonestFormContext } from '@digitools/honest-ui';
import useTranslate from 'src/hooks/useTranslate';
import { CheckboxColumns, LabelText, RadioContainer } from 'src/packages/as/components/LayoutComponents';
import { validateTextArea, isDateInGivenRangeStr, normalizeNewLines } from 'src/packages/as/utils/validationUtils';
import { fieldRequiredStr } from 'src/utils/validationMessages';
import styled from 'styled-components';
import { ConditionCircumstanceText } from '../../constants/ConditionCircumstanceText';
import useAsDataContext from '../../hooks/useAsDataContext';
import useAsContext from '../../hooks/useAsConfigContext';
import { getDropDownList } from '../../utils/AsDataUtils';
import { CustomEmployeeText } from 'src/utils/CustomExperienceText';
import useExperience from 'src/hooks/useExperience';
import { Experience } from 'src/types/Experience';
import HonestInlineDocumentUpload from 'src/components/documentUpload/HonestInlineDocumentUpload';
import { AccommodationValidationLabels } from '../../constants/AccommodationValidationLabels';
import AsVerificationText from '../../i18n/AsVerificationText';
import { AlertInfoIcon } from '@digitools/honest-ui/icons';
const fullWidth = { colSpan: 4, colSpanMd: 6, colSpanLg: 12 };
const datePickerWidth = { colSpan: 4, colSpanMd: 3, colSpanLg: 4 };
const contents = { display: 'contents' };
const radioContainerStyles = {
    marginBottom: '1.5rem',
};
const Disclaimer = styled.div `
    color: ${semantics.color.text.primary};
    font-style: italic;
    font-weight: 100;
    marginTop: 0rem;
    marginLeft: 0.5rem;
    margin-bottom: 1.5rem;
    display: flex;
    align-items: center;
  `;
const ConditionCircumstance = () => {
    const { t } = useTranslate();
    const { conditionData, documents, setDocuments } = useAsDataContext();
    const { asConfig } = useAsContext();
    const { watch } = useHonestFormContext();
    const wrState = watch('conditionData.workRelated');
    const { experience, language } = useExperience();
    let days = asConfig?.config?.requestedStartPastBusinessDays ?
        asConfig?.config?.requestedStartPastBusinessDays :
        asConfig?.config?.requestedStartPastCalendarDays;
    const conditionOptions = getDropDownList(asConfig?.config?.conditionDropDown, t);
    const rlLabel = CustomEmployeeText(experience === Experience.EMPLOYEE ? t(ConditionCircumstanceText.formLabels.reportedRestrictonOrLimitationEE) : t(ConditionCircumstanceText.formLabels.reportedRestrictonOrLimitationER));
    const validateRestrictionTextArea = (value) => validateTextArea(value, AccommodationValidationLabels.reportedRestrictionLimitation)
        ? `${t(AccommodationValidationLabels.reportedRestrictionLimitation)} ${t(AsVerificationText.invalidCharactersMessage)}`
        : undefined;
    return (<div data-public={true} data-testid='accommodation-request-condition'>
      <div style={{ marginTop: 'var(--spacing-xl)' }}>
        <Heading as='h5' weight='bold' className='hui-mb-md' style={{ fontSize: typography.fontSize.lg, lineHeight: typography.lineHeight.xl }}> 
          {t(ConditionCircumstanceText.accommTitle)}
        </Heading>
        <Grid noGutter style={{ rowGap: 0 }}>
            {experience === Experience.EMPLOYER && asConfig?.config?.asRep &&
            <> 
                <GridItem {...fullWidth}>
                  <LabelText htmlFor="receivedDate" data-testid='receivedDateLabel'>{CustomEmployeeText(t(ConditionCircumstanceText.formLabels.receivedDate))}</LabelText>
                </GridItem>
                <GridItem {...datePickerWidth} className='hui-mb-sm'>
                  {/* TODO weird buggy behavior with focus trap of dateInput triggering incorrectly - fix needed in Honest */}
                    <DateInput data-testid='receivedDate' id='receivedDate' name={'conditionData.receivedDate'} label='' hideLabel required={true} requiredMessage={fieldRequiredStr(ConditionCircumstanceText.validationLabels.receivedDate, language)} validate={(value) => isDateInGivenRangeStr(days, value, AccommodationValidationLabels.receivedDate, language)} defaultValue={conditionData?.receivedDate}/>
                  </GridItem>
              </>}
            <GridItem {...fullWidth}>
              <LabelText htmlFor="conditions" optional={true}>{t(ConditionCircumstanceText.formLabels.conditions)} </LabelText>
            </GridItem>
            <GridItem {...fullWidth}>
              <CheckboxGroup name={'conditionData.conditions'} label={t(ConditionCircumstanceText.formLabels.conditions)} hideLabel defaultValue={conditionData?.conditions} className='mb-4'>
                <CheckboxColumns data-private={true}>
                  {conditionOptions.map(option => (<Checkbox key={option.value} value={option.value} label={option.label}/>))}
                </CheckboxColumns>
              </CheckboxGroup>
            </GridItem>
            <GridItem {...fullWidth}>
              <LabelText id='workRelatedLabel' optional={true}>{t(ConditionCircumstanceText.formLabels.workRelated)} </LabelText>
            </GridItem>
            <GridItem {...fullWidth}>
              <RadioGroup name={'conditionData.workRelated'} label={t(ConditionCircumstanceText.formLabels.workRelated)} hideLabel defaultValue={conditionData?.workRelated}>
                <RadioContainer style={radioContainerStyles} data-private={true}>
                  <span style={contents} data-testid={'pref-yes'}>
                    <Radio label={t({ english: 'Yes', spanish: 'Sí' })} value={'Y'}/>
                  </span>
                  <span style={contents} data-testid={'pref-no'}>
                    <Radio label={'No'} value={'N'}/>
                  </span>
                </RadioContainer>
              </RadioGroup>
            </GridItem>
            {(wrState === 'Y') &&
            <GridItem {...fullWidth}>
                <Disclaimer>
                  <AlertInfoIcon className={'mr-2'} width={24} height={24} color={semantics.color.link}/>
                  <div>
                    <strong>{t(ConditionCircumstanceText.formLabels.pleaseNote)}</strong>
                    {t(ConditionCircumstanceText.formLabels.workRelatedMessage)}
                  </div>
                </Disclaimer>
              </GridItem>}
            <GridItem {...fullWidth}>
              <LabelText htmlFor='conditionText'>
                {rlLabel}
              </LabelText>
              <TextArea id='conditionText' rows={3} data-private={true} data-testid='conditionText' label='' hideLabel required={true} requiredMessage={fieldRequiredStr(AccommodationValidationLabels.reportedRestrictionLimitation, language)} name={'conditionData.conditionText'} validate={validateRestrictionTextArea} defaultValue={conditionData?.conditionText} transform={normalizeNewLines}/>
            </GridItem>
            <GridItem {...fullWidth}>
              <LabelText optional={true} htmlFor='availableDocumentation'>
                {t(ConditionCircumstanceText.formLabels.availableDocumentation)}
              </LabelText>
              <HonestInlineDocumentUpload productName={'AS'} documents={documents} setDocuments={setDocuments}/>
            </GridItem>
        </Grid>
      </div>
    </div>);
};
export default React.memo(ConditionCircumstance);
